import React from 'react';
import { motion } from 'framer-motion';
import { Activity, TrendingUp } from 'lucide-react';

const BehavioralHeatmap = ({ behavioralProfile }) => {
  if (!behavioralProfile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No behavioral data available</p>
      </motion.div>
    );
  }

  const { studyConsistency, engagementLevel, procrastinationIndex, attendanceRate, assignmentCompletionRate } = behavioralProfile;

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];

  // Generate heatmap data (simulated based on consistency score)
  const generateHeatmapData = () => {
    const data = [];
    const baseConsistency = studyConsistency.score;
    
    weeks.forEach((week, weekIndex) => {
      days.forEach((day, dayIndex) => {
        const variance = Math.random() * 30 - 15;
        const value = Math.max(0, Math.min(100, baseConsistency + variance));
        data.push({
          week: weekIndex,
          day: dayIndex,
          value,
          label: `${week} - ${day}`
        });
      });
    });
    return data;
  };

  const heatmapData = generateHeatmapData();

  const getHeatmapColor = (value) => {
    if (value >= 80) return 'bg-green-500';
    if (value >= 60) return 'bg-green-400';
    if (value >= 40) return 'bg-yellow-400';
    if (value >= 20) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getConsistencyBadge = (pattern) => {
    const badges = {
      consistent: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', text: 'Consistent' },
      irregular: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', text: 'Irregular' },
      declining: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', text: 'Declining' }
    };
    return badges[pattern] || badges.irregular;
  };

  const badge = getConsistencyBadge(studyConsistency.pattern);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-600 rounded-lg">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Study Consistency</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Your study patterns</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
          {badge.text}
        </span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-3"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Consistency</p>
          <p className="text-2xl font-bold text-green-600">{studyConsistency.score}%</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-3"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Engagement</p>
          <p className="text-2xl font-bold text-blue-600">{engagementLevel.overall}%</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-3"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Attendance</p>
          <p className="text-2xl font-bold text-purple-600">{attendanceRate}%</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-3"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Assignments</p>
          <p className="text-2xl font-bold text-orange-600">{assignmentCompletionRate}%</p>
        </motion.div>
      </div>

      {/* Heatmap */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Study Activity (Last 4 Weeks)
        </h4>
        
        {/* Day labels */}
        <div className="flex gap-1 mb-2 ml-16">
          {days.map((day, index) => (
            <div key={index} className="flex-1 text-center">
              <span className="text-xs text-gray-600 dark:text-gray-400">{day}</span>
            </div>
          ))}
        </div>

        {/* Heatmap grid */}
        <div className="space-y-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex gap-1 items-center">
              <span className="text-xs text-gray-600 dark:text-gray-400 w-14">{week}</span>
              {days.map((day, dayIndex) => {
                const cellData = heatmapData.find(d => d.week === weekIndex && d.day === dayIndex);
                return (
                  <motion.div
                    key={`${weekIndex}-${dayIndex}`}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: (weekIndex * 7 + dayIndex) * 0.02 }}
                    className={`flex-1 aspect-square rounded ${getHeatmapColor(cellData.value)} cursor-pointer hover:scale-110 transition-transform`}
                    title={`${cellData.label}: ${Math.round(cellData.value)}% activity`}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <span className="text-xs text-gray-600 dark:text-gray-400">Less</span>
          <div className="flex gap-1">
            <div className="w-4 h-4 rounded bg-red-400" />
            <div className="w-4 h-4 rounded bg-orange-400" />
            <div className="w-4 h-4 rounded bg-yellow-400" />
            <div className="w-4 h-4 rounded bg-green-400" />
            <div className="w-4 h-4 rounded bg-green-500" />
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400">More</span>
        </div>
      </div>

      {/* Best Study Days */}
      {studyConsistency.bestStudyDays && studyConsistency.bestStudyDays.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-600" />
            Your Best Study Days
          </h4>
          <div className="flex flex-wrap gap-2">
            {studyConsistency.bestStudyDays.map((day, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium"
              >
                {day}
              </motion.span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BehavioralHeatmap;
